import React, { useState, useEffect } from 'react';
import { LeaderboardPeriod, LeaderboardEntry } from '../types/api';
import { leaderboardAPI } from '../utils/api';
import './LeaderboardModal.css';

interface LeaderboardModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: number | null;
  gridSize: 4 | 6 | 8;
}

const LeaderboardModal: React.FC<LeaderboardModalProps> = ({ isOpen, onClose, userId, gridSize }) => {
  const [period, setPeriod] = useState<LeaderboardPeriod>('all-time');
  const [selectedGridSize, setSelectedGridSize] = useState<4 | 6 | 8>(gridSize);
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [userRank, setUserRank] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadLeaderboard();
    }
  }, [isOpen, period, selectedGridSize]);

  useEffect(() => {
    setSelectedGridSize(gridSize);
  }, [gridSize]);

  const loadLeaderboard = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await leaderboardAPI.getLeaderboard(period, 50, selectedGridSize);
      setEntries(response.entries);
      setUserRank(response.userRank);
    } catch (err) {
      console.error('Failed to load leaderboard:', err);
      setError('Failed to load leaderboard. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const periodLabels: Record<LeaderboardPeriod, string> = {
    daily: 'Today',
    weekly: 'This Week',
    monthly: 'This Month',
    'all-time': 'All Time'
  };

  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const gridSizeLabels: Record<4 | 6 | 8, string> = {
    4: '4×4',
    6: '6×6',
    8: '8×8'
  };

  return (
    <div className="leaderboard-overlay" onClick={onClose}>
      <div className="leaderboard-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>

        <h2>Leaderboard</h2>

        <div className="grid-size-tabs">
          {([4, 6, 8] as Array<4 | 6 | 8>).map((size) => (
            <button
              key={size}
              className={`grid-size-tab ${selectedGridSize === size ? 'active' : ''}`}
              onClick={() => setSelectedGridSize(size)}
            >
              {gridSizeLabels[size]}
            </button>
          ))}
        </div>

        <div className="period-tabs">
          {(['daily', 'weekly', 'monthly', 'all-time'] as LeaderboardPeriod[]).map((p) => (
            <button
              key={p}
              className={`period-tab ${period === p ? 'active' : ''}`}
              onClick={() => setPeriod(p)}
            >
              {periodLabels[p]}
            </button>
          ))}
        </div>

        {userRank && (
          <div className="user-rank-banner">
            Your rank: <strong>#{userRank}</strong>
          </div>
        )}

        <div className="leaderboard-content">
          {isLoading ? (
            <div className="loading-state">Loading...</div>
          ) : error ? (
            <div className="error-state">{error}</div>
          ) : entries.length === 0 ? (
            <div className="empty-state">
              No scores yet. Be the first to set a record!
            </div>
          ) : (
            <div className="leaderboard-table">
              <div className="leaderboard-header">
                <div className="col-rank">Rank</div>
                <div className="col-player">Player</div>
                <div className="col-score">Score</div>
              </div>
              {entries.map((entry) => (
                <div
                  key={`${entry.user_id}-${entry.rank}`}
                  className={`leaderboard-row ${entry.user_id === userId ? 'current-user' : ''}`}
                >
                  <div className="col-rank">
                    {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : `#${entry.rank}`}
                  </div>
                  <div className="col-player">
                    <span className="player-name">{entry.name}</span>
                  </div>
                  <div className="col-score">{entry.score.toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaderboardModal;
